/**
 * useUploadQueue.ts
 * Upload queue with retry logic for voice recording chunks
 * Handles network failures and provides progress feedback
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { uploadData } from 'api/apiHelpers';
import { markChunkUploaded, RecordingChunk } from './useIndexedDBBackup';
import logger from "utils/logger";

interface UploadProgress {
  totalChunks: number;
  uploadedChunks: number;
  failedChunks: number;
  isUploading: boolean;
}

const MAX_RETRIES = 5;
const INITIAL_RETRY_DELAY = 1000; // 1 second

export function useUploadQueue() {
  const [progress, setProgress] = useState<UploadProgress>({
    totalChunks: 0,
    uploadedChunks: 0,
    failedChunks: 0,
    isUploading: false,
  });

  const queueRef = useRef<RecordingChunk[]>([]);
  const processingRef = useRef(false);
  // Use ref for immediate access (React state is async)
  const uploadedCountRef = useRef(0);

  /**
   * Add chunk to upload queue
   */
  const enqueue = useCallback((chunk: RecordingChunk) => {
    queueRef.current.push(chunk);
    setProgress((prev) => ({
      ...prev,
      totalChunks: prev.totalChunks + 1,
    }));

    // Start processing if not already running
    if (!processingRef.current) {
      processQueue();
    }
  }, []);

  /**
   * Process the upload queue
   */
  const processQueue = useCallback(async () => {
    if (processingRef.current || queueRef.current.length === 0) {
      return;
    }

    processingRef.current = true;
    setProgress((prev) => ({ ...prev, isUploading: true }));

    while (queueRef.current.length > 0) {
      const chunk = queueRef.current[0];

      try {
        await uploadChunkWithRetry(chunk);
        
        // Mark as uploaded in IndexedDB
        await markChunkUploaded(chunk.sessionId, chunk.chunkIndex);

        // Remove from queue
        queueRef.current.shift();
        
        // Update both ref (immediate) and state (async)
        uploadedCountRef.current += 1;
        setProgress((prev) => ({
          ...prev,
          uploadedChunks: prev.uploadedChunks + 1,
        }));
      } catch (error) {
        logger.error(
          `[UploadQueue] Failed to upload chunk ${chunk.chunkIndex} after ${MAX_RETRIES} retries`,
          error
        );
        
        // Remove from queue but mark as failed
        queueRef.current.shift();
        
        setProgress((prev) => ({
          ...prev,
          failedChunks: prev.failedChunks + 1,
        }));
      }
    }

    processingRef.current = false;
    setProgress((prev) => ({ ...prev, isUploading: false }));
  }, []);

  /**
   * Upload chunk with exponential backoff retry
   */
  const uploadChunkWithRetry = async (
    chunk: RecordingChunk
  ): Promise<void> => {
    let lastError: Error | null = null;

    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        // Check network connectivity
        if (!navigator.onLine) {
          throw new Error('Network offline');
        }

        const formData = new FormData();
        formData.append('session_id', chunk.sessionId);
        formData.append('chunk_index', chunk.chunkIndex.toString());
        formData.append('timestamp', chunk.timestamp.toString());
        formData.append(
          'audio_chunk',
          chunk.blob,
          `chunk_${chunk.chunkIndex.toString().padStart(4, '0')}.webm`
        );

        await uploadData('/recording/chunk/', formData);
        
        logger.debug(
          `[UploadQueue] Successfully uploaded chunk ${chunk.chunkIndex} (attempt ${attempt + 1})`
        );
        
        return; // Success!
      } catch (error: any) {
        lastError = error;
        logger.warn(
          `[UploadQueue] Upload attempt ${attempt + 1}/${MAX_RETRIES} failed for chunk ${chunk.chunkIndex}:`,
          error.message
        );

        // Don't retry on client errors (400-499)
        if (error.response && error.response.status >= 400 && error.response.status < 500) {
          throw error;
        }

        // Wait before retry (exponential backoff)
        if (attempt < MAX_RETRIES - 1) {
          const delay = INITIAL_RETRY_DELAY * Math.pow(2, attempt);
          await new Promise((resolve) => setTimeout(resolve, delay));
        }
      }
    }

    throw lastError || new Error('Upload failed after all retries');
  };

  /**
   * Upload pending chunks from IndexedDB (for recovery)
   */
  const uploadPendingChunks = useCallback(
    async (chunks: RecordingChunk[]) => {
      setProgress({
        totalChunks: chunks.length,
        uploadedChunks: 0,
        failedChunks: 0,
        isUploading: true,
      });

      queueRef.current = [...chunks];
      await processQueue();
    },
    [processQueue]
  );

  /**
   * Reset queue state
   */
  const reset = useCallback(() => {
    queueRef.current = [];
    uploadedCountRef.current = 0;
    setProgress({
      totalChunks: 0,
      uploadedChunks: 0,
      failedChunks: 0,
      isUploading: false,
    });
    processingRef.current = false;
  }, []);

  /**
   * Get uploaded count immediately (bypasses React state)
   */
  const getUploadedCount = useCallback(() => uploadedCountRef.current, []);

  /**
   * Wait for all uploads to complete
   */
  const waitForUploads = useCallback(async (expectedCount: number, timeoutMs: number = 60000) => {
    const startTime = Date.now();
    while (uploadedCountRef.current < expectedCount) {
      if (Date.now() - startTime > timeoutMs) {
        logger.error(`[UploadQueue] Timeout waiting for uploads: ${uploadedCountRef.current}/${expectedCount}`);
        return false;
      }
      await new Promise(r => setTimeout(r, 200));
    }
    return true;
  }, []);

  // Monitor network status
  useEffect(() => {
    const handleOnline = () => {
      logger.debug('[UploadQueue] Network back online, resuming uploads...');
      if (queueRef.current.length > 0 && !processingRef.current) {
        processQueue();
      }
    };

    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [processQueue]);

  return {
    enqueue,
    uploadPendingChunks,
    reset,
    progress,
    getUploadedCount,
    waitForUploads,
  };
}
